import React from 'react';
import clsx from 'clsx';
import { MBrandElement } from '../MBrandElement/index';
import { MTextBlockFull } from '../MTextBlockFull/index';
import { MTextBlockFullAlignment } from '../MTextBlockFull/MTextBlockFull.types';
import { AAccentLineSize } from '../../atoms/AAccentLine/AAccentLine.types';
import { MButton } from '../MButton/index';
import { MButtonSize, type MButtonProps } from '../MButton/MButton.types';
import * as Styled from './MContentBlock.styles';
import { MContentBlockAlignment, type MContentBlockFlag, type MContentBlockProps } from './MContentBlock.types';

const COMPONENT_NAME = 'm-web-ui-kit-MContentBlock';
const TEST_ID = 'web-ui-kit-MContentBlock';

const isTruthyFlag = (value?: MContentBlockFlag): boolean => value === true || value === 'true';

const isFlagOff = (value?: MContentBlockFlag): boolean => value === false || value === 'false';

const hasButtonContent = (item?: MButtonProps): boolean =>
  !!item && !!(item.buttonText || item.children || item.ctaLink || item.href);

const mapAlignment = (alignment: MContentBlockAlignment): MTextBlockFullAlignment =>
  alignment === MContentBlockAlignment.Centered ? MTextBlockFullAlignment.Centered : MTextBlockFullAlignment.Left;

export const MContentBlock: React.FC<MContentBlockProps> = ({
  alignment = MContentBlockAlignment.Left,
  showBrandElement,
  buttonGroup,
  secondaryButton,
  mBrandElement,
  mTextBlockFull,
  mButton,
  mButton2,
  className,
  ...rest
}) => {
  const mappedAlignment = mapAlignment(alignment);
  const showBrand = !!mBrandElement && !isFlagOff(showBrandElement);
  const showButtonGroup = isTruthyFlag(buttonGroup);
  const showPrimaryButton = showButtonGroup && hasButtonContent(mButton);
  const showSecondaryButton =
    showButtonGroup && hasButtonContent(mButton2) && (secondaryButton === undefined || isTruthyFlag(secondaryButton));

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('m-content-block', className, alignment)}
      $alignment={alignment}
      {...rest}
    >
      {showBrand && mBrandElement && (
        <Styled.BrandWrapper $alignment={alignment}>
          <MBrandElement {...mBrandElement} />
        </Styled.BrandWrapper>
      )}

      {mTextBlockFull && (
        <MTextBlockFull
          {...mTextBlockFull}
          alignment={mappedAlignment}
          aAccentLine={{
            ...(mTextBlockFull.aAccentLine ?? { size: AAccentLineSize.OnePx }),
            'aria-hidden': true,
          }}
        />
      )}

      {(showPrimaryButton || showSecondaryButton) && (
        <Styled.ButtonGroup $alignment={alignment}>
          {showPrimaryButton && mButton && <MButton size={MButtonSize.S} {...mButton} />}
          {showSecondaryButton && mButton2 && <MButton size={MButtonSize.S} {...mButton2} />}
        </Styled.ButtonGroup>
      )}
    </Styled.Wrapper>
  );
};

export { MContentBlockAlignment, TEST_ID as M_CONTENT_BLOCK_TEST_ID };
export type { MContentBlockFlag, MContentBlockProps } from './MContentBlock.types';
